/**
 * @fileoverview Accessible source-owned button primitive with semantic variants.
 * @provides Button and buttonVariants for actions and link composition.
 * @usedBy Forms, navigation, lead actions, dialogs, and administrator controls.
 * @uses Radix Slot, Class Variance Authority, and the shared class helper.
 * @accessibility Maintains minimum touch size and visible disabled/pending states.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

export const buttonVariants = cva("inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50", { variants: { variant: { default: "bg-primary text-primary-foreground hover:opacity-90", secondary: "bg-secondary text-secondary-foreground hover:opacity-90", outline: "border bg-card hover:bg-accent", destructive: "bg-destructive text-destructive-foreground hover:opacity-90", ghost: "hover:bg-accent" }, size: { default: "h-11", sm: "h-10 px-3", icon: "size-11 p-0" } }, defaultVariants: { variant: "default", size: "default" } });
type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants> & { asChild?: boolean };
export const Button = React.forwardRef<HTMLButtonElement, Props>(({ className, variant, size, asChild, ...props }, ref) => {
  const Component = asChild ? Slot : "button";
  return <Component ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});
Button.displayName = "Button";
